import { useQuery } from '@apollo/client';
import { serverSideCriteriaProcessing } from '@openx/components-table/serverSideCriteriaProcessing';
import type { QueryVar } from '@openx/types/serverSideCriteria';
import { type CriteriaChange, CriteriaDimension } from '@openx/types/tableCriteria';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria';
import { adjustPaginationCriteria } from '@openx/utils/lib/tableHelpers';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo } from 'react';
import { useAuth } from '../../../../components/AuthContext/context';
import { GetAudiencesListDocument } from '../../../../graphql/query/audiences/GetAudiencesList.generated';
import type { GetAudiencesTotalQueryVariables } from '../../../../graphql/query/audiences/GetAudiencesTotal.generated';
import { useListCriteria } from '../../../../utils/useListCriteria';
import { audiencesDefaultCriteria } from '../constants';
import type { AudiencesListFilters } from '../types';
import { adjustCriteriaFilters } from '../utils/adjustCriteriaFilters';

export const useAudienceListFetch = () => {
  const { session } = useAuth();
  const { criteria, setCriteria } = useListCriteria('audiences', audiencesDefaultCriteria);
  const queryFilters = useMemo(() => adjustCriteriaFilters(criteria), [criteria]);

  const queryCrit = serverSideCriteriaProcessing<GetAudiencesTotalQueryVariables & QueryVar>(
    { ...criteria, filters: queryFilters },
    [audiencesDefaultCriteria.sort]
  );

  const { data, loading, refetch, error } = useQuery(GetAudiencesListDocument, {
    onCompleted: res => {
      const aggregatedCount = res.audience_aggregate.aggregate?.count;

      if (typeof aggregatedCount !== 'undefined') {
        setCriteria(previousCriteria => adjustPaginationCriteria(previousCriteria, aggregatedCount, false));
      }
    },
    variables: {
      ...queryCrit,
      offset: criteria.pagination ? (criteria.pagination.pageNumber - 1) * criteria.pagination.pageSize : undefined,
    },
  });

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension, AudiencesListFilters>) => {
      setCriteria(previousCriteria => applyCriteriaChange(previousCriteria, newCriteria));
    },
    [setCriteria]
  );

  const handleFiltersChange = useCallback(
    (newFilters: AudiencesListFilters) => {
      const previousFilters = criteria.filters as AudiencesListFilters;
      const { my_audiences, createdBy = [], ...rest } = newFilters;
      const hasMyAudienceChanged = my_audiences !== previousFilters?.my_audiences;
      const hasCreatedByChanged = !isEqual(createdBy, previousFilters?.createdBy);

      let remappedFilters: AudiencesListFilters = { ...newFilters };

      if (hasMyAudienceChanged) {
        if (my_audiences) {
          remappedFilters = {
            ...rest,
            createdBy: [session?.id],
            my_audiences,
          };
        } else {
          const cleanedCreatedBy = createdBy.filter(userId => userId !== session?.id);

          remappedFilters = {
            ...rest,
            createdBy: cleanedCreatedBy.length ? cleanedCreatedBy : undefined,
            my_audiences: undefined,
          };
        }
      } else if (hasCreatedByChanged) {
        const includesSessionUser = createdBy.includes(session?.id);

        remappedFilters = {
          ...rest,
          createdBy: createdBy.length ? createdBy : undefined,
          my_audiences: includesSessionUser ? session?.id : undefined,
        };
      }

      handleCriteriaChange({
        dimension: CriteriaDimension.FILTERS,
        value: remappedFilters,
      });
    },
    [handleCriteriaChange, criteria.filters, session?.id]
  );

  const audiences = useMemo(() => data?.audience ?? [], [data?.audience]);

  return {
    criteria,
    audiences,
    handleCriteriaChange,
    handleFiltersChange,
    loading,
    refetch,
    error,
  };
};
